import type { Router } from 'vue-router'

const NAV_TIMEOUT_MS = 8_000

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(
    message,
  )
}

/** An open dialog that declares itself modal. Its backdrop may be a child layer. */
function hasOpenModal(): boolean {
  for (const el of document.querySelectorAll<HTMLElement>('[aria-modal="true"]')) {
    const style = getComputedStyle(el)
    if (style.display !== 'none' && style.visibility !== 'hidden') return true
  }
  return false
}

/** A real modal or backdrop is on screen. Invisible locks are not. */
export function hasPaintedOverlay(): boolean {
  if (hasOpenModal()) return true
  for (const el of document.querySelectorAll<HTMLElement>('body *')) {
    const style = getComputedStyle(el)
    if (style.position !== 'fixed') continue
    if (isPainted(el, style)) return true
  }
  return false
}

function isPainted(el: HTMLElement, style: CSSStyleDeclaration): boolean {
  if (style.display === 'none' || style.visibility === 'hidden') return false
  if (Number(style.opacity) < 0.2) return false
  if (style.pointerEvents === 'none') return false
  const rect = el.getBoundingClientRect()
  if (rect.width < window.innerWidth * 0.5 || rect.height < window.innerHeight * 0.5) return false
  return alphaOf(style.backgroundColor) >= 0.05
}

function alphaOf(color: string): number {
  if (!color || color === 'transparent') return 0
  const match = color.match(/rgba?\(([^)]+)\)/i)
  if (!match) return 1
  const parts = match[1].split(',').map((part) => part.trim())
  if (parts.length < 4) return 1
  const alpha = Number(parts[3])
  return Number.isFinite(alpha) ? alpha : 1
}

function coversViewport(el: HTMLElement): boolean {
  const style = getComputedStyle(el)
  if (style.position !== 'fixed' || style.pointerEvents === 'none') return false
  const rect = el.getBoundingClientRect()
  return rect.width > window.innerWidth * 0.8 && rect.height > window.innerHeight * 0.8
}

/**
 * Dialogs mark `#app` inert and paint full-viewport layers. If one never
 * finishes closing, nav clicks stop landing until a refresh. Release that
 * lock only when nothing visible is actually open.
 */
export function releaseStaleInteractionBlock(): boolean {
  if (hasPaintedOverlay()) return false

  const app = document.getElementById('app')
  let released = false

  if (app?.inert) {
    app.inert = false
    released = true
  }
  if (app?.getAttribute('aria-hidden') === 'true') {
    app.removeAttribute('aria-hidden')
    released = true
  }

  const rootStyle = document.documentElement.style
  if (rootStyle.overflow === 'hidden') {
    rootStyle.overflow = ''
    released = true
  }
  if (rootStyle.paddingRight) {
    rootStyle.paddingRight = ''
    released = true
  }

  for (const el of document.querySelectorAll<HTMLElement>('body *')) {
    if (!coversViewport(el)) continue
    el.style.pointerEvents = 'none'
    released = true
  }

  return released
}

function isPlainLeftClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

export function installNavigationRecovery(router: Router) {
  const flag = '__lighthouseNavRecovery'
  if (flag in window) return
  Object.defineProperty(window, flag, { value: true })

  let timer = 0
  let pendingPath = ''

  router.beforeEach((to) => {
    window.clearTimeout(timer)
    pendingPath = to.fullPath
    // A stale dev-server or CDN connection can leave a lazy route pending
    // forever. A full load is what a manual refresh does.
    if (!import.meta.env.PROD) return
    timer = window.setTimeout(() => {
      if (router.currentRoute.value.fullPath !== pendingPath) {
        window.location.assign(pendingPath)
      }
    }, NAV_TIMEOUT_MS)
  })

  router.afterEach(() => {
    window.clearTimeout(timer)
  })

  router.onError((error, to) => {
    if (!isChunkLoadError(error)) return
    const path = to?.fullPath || pendingPath
    if (path) window.location.assign(path)
  })

  window.addEventListener(
    'pointerdown',
    (event) => {
      if (!releaseStaleInteractionBlock()) return
      window.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }),
      )
      if (!isPlainLeftClick(event)) return
      const hit = document.elementFromPoint(event.clientX, event.clientY)
      const link = hit instanceof Element ? hit.closest('a[href]') : null
      if (!(link instanceof HTMLAnchorElement)) return
      if (link.target && link.target !== '_self') return
      if (link.hasAttribute('download')) return
      let url: URL
      try {
        url = new URL(link.href, window.location.origin)
      } catch {
        return
      }
      if (url.origin !== window.location.origin) return
      event.preventDefault()
      event.stopPropagation()
      void router.push(url.pathname + url.search + url.hash)
    },
    true,
  )

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') releaseStaleInteractionBlock()
  })
}

/** Don't call other auth methods inside onAuthStateChange; it holds the auth lock. */
export function deferAuthWork(work: () => void) {
  window.setTimeout(work, 0)
}

export function withTimeout(task: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(), ms)
    task.then(
      () => {
        window.clearTimeout(timer)
        resolve()
      },
      () => {
        window.clearTimeout(timer)
        resolve()
      },
    )
  })
}
